/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react";
import { onSwiftRefresh } from "@src/components/modules/TemplateModule/TopBar/refresh";
import { useNavigate } from "react-router";

import MainTemplate from "@src/components/modules/TemplateModule/MainTemplate";
import Navigation from "@src/components/modules/NavigationModule/Navigation";
import FilterList from "@src/components/ui/Lists/FilterList";
import UserListItem from "@src/components/modules/UserModule/UserListItem";

import type { User } from "@src/@types/User";

import projectStore from "@src/stores/ProjectStore";
import userStore from "@src/stores/UserStore";
import configLoader from "@src/utils/Config";
import PageHeader from "@src/components/smart/PageHeader";
import { USER_COLUMNS } from "@src/components/modules/UserModule/UserListItem/UserListItem";
import {
  ClearFiltersButton,
  FilterSelect,
  Segmented,
  ToolbarSearch,
  matchesSearch,
  useListQuery,
} from "@src/components/ui/Lists/ListToolbar";
import type { ListQuery } from "@src/components/ui/Lists/ListToolbar";
import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";
import { Users } from "lucide-react";

const Wrapper = styled.div<any>``;

type State = {
  modalIsOpen: boolean;
};

type Props = {
  onNavigate: (path: string) => void;
  query: ListQuery;
};

@observer
class UsersPage extends React.Component<Props, State> {
  state = {
    modalIsOpen: false,
  };

  pollTimeout = 0;

  stopPolling = false;

  offSwiftRefresh?: () => void;

  componentDidMount() {
    // The top bar's Refresh reloads this page's data.
    this.offSwiftRefresh = onSwiftRefresh(() => {
      this.handleReloadButtonClick();
    });
    document.title = "Users · SwiftIQ";

    projectStore.getProjects();
    userStore.getAllUsers();

    this.stopPolling = false;
    this.pollData(true);
  }

  componentWillUnmount() {
    this.offSwiftRefresh?.();
    clearTimeout(this.pollTimeout);
    this.stopPolling = true;
  }

  getProjectName(projectId?: string | null): string {
    if (!projectId) {
      return "-";
    }
    const project = projectStore.projects.find(p => p.id === projectId);
    return project ? project.name : "-";
  }

  handleModalOpen() {
    this.setState({ modalIsOpen: true });
  }

  handleModalClose() {
    this.setState({ modalIsOpen: false }, () => {
      this.pollData();
    });
  }

  handleReloadButtonClick() {
    projectStore.getProjects();
    userStore.getAllUsers({ showLoading: true });
  }

  async pollData(showLoading?: boolean) {
    if (this.state.modalIsOpen || this.stopPolling) {
      return;
    }

    await userStore.getAllUsers({ showLoading, skipLog: true });
    this.pollTimeout = window.setTimeout(() => {
      this.pollData();
    }, configLoader.config.requestPollTimeout);
  }

  get filtersActive() {
    const q = this.props.query;
    return Boolean(q.get("q") || q.get("enabled") || q.get("project"));
  }

  clearFilters() {
    this.props.query.set({ q: null, enabled: null, project: null });
  }

  filteredUsers(except?: string): User[] {
    const q = this.props.query;
    const text = q.get("q");
    const enabled = q.get("enabled");
    const project = q.get("project");
    return userStore.users
      .filter(u => {
        if (except !== "enabled" && enabled === "yes" && u.enabled === false) return false;
        if (except !== "enabled" && enabled === "no" && u.enabled !== false) return false;
        if (project && u.project_id !== project) return false;
        if (text) {
          return matchesSearch(text, [u.name, u.email, u.description, this.getProjectName(u.project_id)]);
        }
        return true;
      })
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  renderToolbar() {
    const q = this.props.query;
    const forEnabled = this.filteredUsers("enabled");
    const projects = projectStore.projects
      .map(p => ({ label: p.name, value: p.id, count: userStore.users.filter(u => u.project_id === p.id).length }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return (
      <>
        <ToolbarSearch value={q.get("q")} placeholder="Search name, email or project" onChange={v => q.set({ q: v })} />
        <Segmented
          label="Status"
          value={q.get("enabled")}
          onChange={v => q.set({ enabled: v })}
          options={[
            { value: "", label: "All" },
            { value: "yes", label: "Enabled", count: forEnabled.filter(u => u.enabled !== false).length },
            { value: "no", label: "Disabled", count: forEnabled.filter(u => u.enabled === false).length },
          ]}
        />
        <FilterSelect
          label="Project"
          value={q.get("project")}
          onChange={v => q.set({ project: v })}
          options={projects}
          allLabel="Any"
        />
        {this.filtersActive ? <ClearFiltersButton onClick={() => this.clearFilters()} /> : null}
      </>
    );
  }

  rowActions(user: User): DropdownAction[] {
    return [
      { label: "Open", action: () => this.props.onNavigate(`/users/${user.id}`) },
      {
        label: "Open primary project",
        hidden: !user.project_id,
        action: () => this.props.onNavigate(`/projects/${user.project_id}`),
      },
    ];
  }

  render() {

    return (
      <Wrapper>
        <MainTemplate
          navigationComponent={<Navigation currentPage="users" />}
          listNoMargin
          listComponent={
            <FilterList
              toolbar={this.renderToolbar()}
              filtersActive={this.filtersActive}
              onClearFilters={() => this.clearFilters()}
              columns={USER_COLUMNS}
              selectionLabel="user"
              loading={userStore.allUsersLoading}
              items={this.filteredUsers()}
              onItemClick={(user: User) => {
                this.props.onNavigate(`/users/${user.id}`);
              }}
              onReloadButtonClick={() => {
                this.handleReloadButtonClick();
              }}
              renderItemComponent={component => (
                <UserListItem
                  {...component}
                  getProjectName={projectId => this.getProjectName(projectId)}
                  actions={this.rowActions(component.item)}
                />
              )}
              emptyListIcon={Users}
              emptyListMessage="No users yet"
              emptyListExtraMessage="Add the people who run migrations from the New menu."
              itemsPerPageOptions={[10, 25, 50]}
              initialItemsPerPage={10}
            />
          }
          headerComponent={
            <PageHeader
              title="Users"
              subtitle="The people who can sign in to SwiftIQ, and their projects."
              onModalOpen={() => {
                this.handleModalOpen();
              }}
              onModalClose={() => {
                this.handleModalClose();
              }}
            />
          }
        />
      </Wrapper>
    );
  }
}

function UsersPageWithNavigate() {
  const navigate = useNavigate();
  const query = useListQuery();

  return <UsersPage onNavigate={navigate} query={query} />;
}

export default UsersPageWithNavigate;
